import type { FlightDataViewerDb } from './db';
import { db as defaultDb } from './db';
import { orderEntriesChronologically } from './order';
import type {
  Aircraft,
  Flight,
  LogbookCheck,
  LogbookEntry,
  Setting,
  Tombstone,
  TombstoneTable,
  Track,
  TrackFile,
  TrackPoints,
} from './types';

/**
 * Deletes and puts that a sync folder can follow.
 *
 * Every delete below leaves a `Tombstone` in the same transaction, and every
 * put clears one, so "deleted here" survives long enough to be written into
 * this device's sync file and read by the others. `deletedAt` defaults to now;
 * a merge passes the other device's own time so a deletion keeps the instant
 * it actually happened, not the instant it arrived.
 */
function tombstoneKey(table: TombstoneTable, id: string): string {
  return `${table}:${id}`;
}

async function writeTombstone(
  db: FlightDataViewerDb,
  table: TombstoneTable,
  id: string,
  deletedAt: string = new Date().toISOString(),
): Promise<void> {
  const key = tombstoneKey(table, id);
  const existing = await db.tombstones.get(key);
  if (existing === undefined || existing.deletedAt < deletedAt) {
    await db.tombstones.put({ key, table, id, deletedAt });
  }
}

/**
 * The seam between the app and Dexie.
 *
 * `ARCHITECTURE.md` §1 keeps SQLite-WASM open as an upgrade if logbook
 * aggregation gets slow. That swap is only cheap if features never touch Dexie
 * directly — so they import repositories, and this is the only file that knows
 * what the storage engine is.
 *
 * Each repository takes its database, defaulting to the singleton, so tests can
 * hand it a throwaway instance instead of sharing global state.
 */

export class TrackRepository {
  private readonly db: FlightDataViewerDb;

  constructor(db: FlightDataViewerDb = defaultDb) {
    this.db = db;
  }

  async get(id: string): Promise<Track | undefined> {
    return this.db.tracks.get(id);
  }

  /** Dedupe lookup: the hash of the normalised points, not of the file bytes. */
  async findByContentHash(contentHash: string): Promise<Track | undefined> {
    return this.db.tracks.where('contentHash').equals(contentHash).first();
  }

  /**
   * Every track, most recently flown first — for work over the whole table,
   * like the batch image export. Its own method rather than
   * `listRecent(Number.MAX_SAFE_INTEGER)`: IndexedDB's `getAll` count is an
   * unsigned 32-bit integer and throws on anything larger.
   */
  async list(): Promise<Track[]> {
    return this.db.tracks.orderBy('startTime').reverse().toArray();
  }

  /** When the first track still stored was imported, or `null` with none — off the `importedAt` index. */
  async firstImportedAt(): Promise<string | null> {
    return (await this.db.tracks.orderBy('importedAt').first())?.importedAt ?? null;
  }

  /** Most recently flown first — the order the flight list wants. */
  async listRecent(limit = 50): Promise<Track[]> {
    return this.db.tracks.orderBy('startTime').reverse().limit(limit).toArray();
  }

  /**
   * Tracks whose time interval overlaps `[startTime, endTime]`.
   *
   * The second half of duplicate detection. `contentHash` catches the same file
   * imported twice, but not the same flight arriving as an FR24 export *and* an
   * EFB GPX — different fixes, different hash, one flight. Those only look alike
   * in time and registration.
   *
   * Both paths narrow with an index and then decide the overlap in memory,
   * because a track that *starts* before the window can still overlap it and no
   * single index range expresses that. With a registration, the
   * `registration` index gives every track for that aircraft — a few hundred
   * for one aeroplane, and the far more selective key. Without one, the
   * `startTime` index gives anything starting within a day either side, which
   * is more than any light-aircraft flight and still a bounded scan.
   *
   * @param registration  when given, narrows to that aircraft.
   */
  async findOverlapping(
    startTime: string,
    endTime: string,
    registration?: string,
  ): Promise<Track[]> {
    const dayMs = 86_400_000;
    const from = new Date(Date.parse(startTime) - dayMs).toISOString();
    const to = new Date(Date.parse(endTime) + dayMs).toISOString();

    const candidates =
      registration === undefined
        ? await this.db.tracks.where('startTime').between(from, to, true, true).toArray()
        : await this.db.tracks.where('registration').equals(registration).toArray();

    return candidates.filter((track) => track.startTime <= endTime && track.endTime >= startTime);
  }

  /**
   * Writes the track, its points and the files it came from in one
   * transaction: a track without points is useless, and a track whose
   * original silently failed to store is one a pilot believes is kept and
   * is not.
   */
  async add(track: Track, points: TrackPoints, files: readonly TrackFile[] = []): Promise<string> {
    const { tracks, trackPoints, trackFiles, tombstones } = this.db;
    return this.db.transaction('rw', tracks, trackPoints, trackFiles, tombstones, async () => {
      await tracks.add(track);
      await tombstones.delete(tombstoneKey('tracks', track.id));
      await trackPoints.add({ ...points, trackId: track.id });
      if (files.length > 0) {
        await trackFiles.bulkAdd(files.map((file) => ({ ...file, trackId: track.id })));
      }
      return track.id;
    });
  }

  async getPoints(trackId: string): Promise<TrackPoints | undefined> {
    return this.db.trackPoints.get(trackId);
  }

  /** The track goes with its points and its original files; none of them means anything alone. */
  async delete(id: string, deletedAt?: string): Promise<void> {
    const { tracks, trackPoints, trackFiles, tombstones } = this.db;
    await this.db.transaction('rw', tracks, trackPoints, trackFiles, tombstones, async () => {
      await trackFiles.where('trackId').equals(id).delete();
      await trackPoints.delete(id);
      await tracks.delete(id);
      await writeTombstone(this.db, 'tracks', id, deletedAt);
    });
  }

  async count(): Promise<number> {
    return this.db.tracks.count();
  }
}

/**
 * The original files behind each track — `TrackFile`.
 *
 * Written by `TrackRepository.add` alongside a new track; this repository is
 * for reading them back and for attaching a file to a track that already
 * exists, which is what a duplicate drop and a backup restore both do.
 */
export class TrackFileRepository {
  private readonly db: FlightDataViewerDb;

  constructor(db: FlightDataViewerDb = defaultDb) {
    this.db = db;
  }

  async get(id: string): Promise<TrackFile | undefined> {
    return this.db.trackFiles.get(id);
  }

  /** Oldest first — the file the track was first imported from leads. */
  async listByTrack(trackId: string): Promise<TrackFile[]> {
    const files = await this.db.trackFiles.where('trackId').equals(trackId).toArray();
    return files.sort((a, b) => a.addedAt.localeCompare(b.addedAt));
  }

  async list(): Promise<TrackFile[]> {
    return this.db.trackFiles.toArray();
  }

  /**
   * Attach a file to an existing track, unless that track already holds the
   * same bytes.
   *
   * @returns whether it was stored.
   */
  async attach(file: TrackFile): Promise<boolean> {
    return this.db.transaction('rw', this.db.trackFiles, async () => {
      const same = await this.db.trackFiles.where('sha256').equals(file.sha256).toArray();
      if (same.some((existing) => existing.trackId === file.trackId)) {
        return false;
      }
      await this.db.trackFiles.add(file);
      return true;
    });
  }

  async count(): Promise<number> {
    return this.db.trackFiles.count();
  }
}

export class FlightRepository {
  private readonly db: FlightDataViewerDb;

  constructor(db: FlightDataViewerDb = defaultDb) {
    this.db = db;
  }

  async get(id: string): Promise<Flight | undefined> {
    return this.db.flights.get(id);
  }

  async put(flight: Flight): Promise<string> {
    const { flights, tombstones } = this.db;
    return this.db.transaction('rw', flights, tombstones, async () => {
      await tombstones.delete(tombstoneKey('flights', flight.id));
      return flights.put(flight);
    });
  }

  /** Every flight, unordered — the logbook grid's join to `derived.confidence` for its own rows. */
  async list(): Promise<Flight[]> {
    return this.db.flights.toArray();
  }

  /** Inclusive on both ends; `from`/`to` are local calendar dates. */
  async listByDateRange(from: string, to: string): Promise<Flight[]> {
    return this.db.flights.where('date').between(from, to, true, true).toArray();
  }

  async listByAircraft(aircraftId: string): Promise<Flight[]> {
    return this.db.flights.where('aircraftId').equals(aircraftId).toArray();
  }

  /** Most recently flown first — the order the flight list reads in. */
  async listRecent(limit = 500): Promise<Flight[]> {
    return this.db.flights.orderBy('date').reverse().limit(limit).toArray();
  }

  async findByTrack(trackId: string): Promise<Flight | undefined> {
    return this.db.flights.where('trackId').equals(trackId).first();
  }

  /**
   * Every flight pointing at this track.
   *
   * `findByTrack` returns the first, which is all a correct database has —
   * this one exists so the link editor can *prove* that, rather than assume
   * it, before moving a track from one flight to another.
   */
  async listByTrack(trackId: string): Promise<Flight[]> {
    return this.db.flights.where('trackId').equals(trackId).toArray();
  }

  async delete(id: string, deletedAt?: string): Promise<void> {
    const { flights, tombstones } = this.db;
    await this.db.transaction('rw', flights, tombstones, async () => {
      await flights.delete(id);
      await writeTombstone(this.db, 'flights', id, deletedAt);
    });
  }

  async count(): Promise<number> {
    return this.db.flights.count();
  }
}

export class AircraftRepository {
  private readonly db: FlightDataViewerDb;

  constructor(db: FlightDataViewerDb = defaultDb) {
    this.db = db;
  }

  async get(id: string): Promise<Aircraft | undefined> {
    return this.db.aircraft.get(id);
  }

  async findByRegistration(registration: string): Promise<Aircraft | undefined> {
    return this.db.aircraft.where('registration').equals(registration).first();
  }

  async list(): Promise<Aircraft[]> {
    return this.db.aircraft.orderBy('registration').toArray();
  }

  async put(record: Aircraft): Promise<string> {
    const { aircraft, tombstones } = this.db;
    return this.db.transaction('rw', aircraft, tombstones, async () => {
      await tombstones.delete(tombstoneKey('aircraft', record.id));
      return aircraft.put(record);
    });
  }

  async delete(id: string, deletedAt?: string): Promise<void> {
    const { aircraft, tombstones } = this.db;
    await this.db.transaction('rw', aircraft, tombstones, async () => {
      await aircraft.delete(id);
      await writeTombstone(this.db, 'aircraft', id, deletedAt);
    });
  }

  async count(): Promise<number> {
    return this.db.aircraft.count();
  }
}

export class LogbookRepository {
  private readonly db: FlightDataViewerDb;

  constructor(db: FlightDataViewerDb = defaultDb) {
    this.db = db;
  }

  async get(id: string): Promise<LogbookEntry | undefined> {
    return this.db.logbookEntries.get(id);
  }

  async put(entry: LogbookEntry): Promise<string> {
    const { logbookEntries, tombstones } = this.db;
    return this.db.transaction('rw', logbookEntries, tombstones, async () => {
      await tombstones.delete(tombstoneKey('logbookEntries', entry.id));
      return logbookEntries.put(entry);
    });
  }

  async delete(id: string, deletedAt?: string): Promise<void> {
    const { logbookEntries, tombstones } = this.db;
    await this.db.transaction('rw', logbookEntries, tombstones, async () => {
      await logbookEntries.delete(id);
      await writeTombstone(this.db, 'logbookEntries', id, deletedAt);
    });
  }

  /**
   * Every entry, oldest first — the order a logbook page and a running total
   * both read in.
   *
   * Sorted in memory rather than left to `orderBy('date')`, which only orders
   * *between* days and hands back a single day's flights in primary-key order.
   * `compareEntriesChronologically` is what that promise actually requires —
   * joined here to each entry's `Flight` so that promise is the actual
   * takeoff time whenever one is known, not just creation order.
   */
  async listOrdered(): Promise<LogbookEntry[]> {
    const [entries, allFlights] = await Promise.all([
      this.db.logbookEntries.orderBy('date').toArray(),
      this.db.flights.toArray(),
    ]);
    const flightById = new Map(allFlights.map((flight) => [flight.id, flight]));
    return orderEntriesChronologically(entries, flightById);
  }

  async findByFlight(flightId: string): Promise<LogbookEntry | undefined> {
    return this.db.logbookEntries.where('flightId').equals(flightId).first();
  }

  async listByAircraft(aircraftId: string): Promise<LogbookEntry[]> {
    return this.db.logbookEntries.where('aircraftId').equals(aircraftId).toArray();
  }

  /** The dedup lookup for draft-and-approve: same aircraft, same calendar day. */
  async listByAircraftAndDate(aircraftId: string, date: string): Promise<LogbookEntry[]> {
    return this.db.logbookEntries.where('[aircraftId+date]').equals([aircraftId, date]).toArray();
  }

  async count(): Promise<number> {
    return this.db.logbookEntries.count();
  }
}

/**
 * The formal checks — flight tests, reviews, competency checks.
 *
 * Its own repository rather than a corner of `LogbookRepository`, for the
 * same reason `LogbookCheck` is its own record: the questions asked of it
 * are different ones. A logbook is read in date order and folded into
 * totals; this table is read the other way round, newest first, because
 * what a pilot wants from it is "when does the next one fall due".
 */
export class LogbookCheckRepository {
  private readonly db: FlightDataViewerDb;

  constructor(db: FlightDataViewerDb = defaultDb) {
    this.db = db;
  }

  async get(id: string): Promise<LogbookCheck | undefined> {
    return this.db.logbookChecks.get(id);
  }

  async put(check: LogbookCheck): Promise<string> {
    const { logbookChecks, tombstones } = this.db;
    return this.db.transaction('rw', logbookChecks, tombstones, async () => {
      await tombstones.delete(tombstoneKey('logbookChecks', check.id));
      return logbookChecks.put(check);
    });
  }

  async delete(id: string, deletedAt?: string): Promise<void> {
    const { logbookChecks, tombstones } = this.db;
    await this.db.transaction('rw', logbookChecks, tombstones, async () => {
      await logbookChecks.delete(id);
      await writeTombstone(this.db, 'logbookChecks', id, deletedAt);
    });
  }

  /** Most recent first — the order the checks page reads in. */
  async listRecent(): Promise<LogbookCheck[]> {
    return this.db.logbookChecks.orderBy('date').reverse().toArray();
  }

  /** The checks flown on one flight, for the link back from a logbook row. */
  async listByFlight(flightId: string): Promise<LogbookCheck[]> {
    return this.db.logbookChecks.where('flightId').equals(flightId).toArray();
  }

  async count(): Promise<number> {
    return this.db.logbookChecks.count();
  }
}

export class SettingsRepository {
  private readonly db: FlightDataViewerDb;

  constructor(db: FlightDataViewerDb = defaultDb) {
    this.db = db;
  }

  async get<T>(key: string, fallback: T): Promise<T> {
    const row = (await this.db.settings.get(key)) as Setting<T> | undefined;
    return row === undefined ? fallback : row.value;
  }

  async set(key: string, value: unknown): Promise<void> {
    await this.db.settings.put({ key, value, updatedAt: new Date().toISOString() });
  }

  /** Every row as stored, `updatedAt` included — what a backup and a sync merge need. */
  async listRows(): Promise<Setting[]> {
    return this.db.settings.toArray();
  }

  /**
   * Write a row exactly as given, `updatedAt` and all. For a merge only: a
   * newest-wins comparison is meaningless if applying the winner restamps it.
   */
  async putRow(row: Setting): Promise<void> {
    await this.db.settings.put(row);
  }

  /** Every stored setting, keyed by its own key — `useSettings` merges this onto the defaults. */
  async getAll(): Promise<Record<string, unknown>> {
    const rows = await this.db.settings.toArray();
    return Object.fromEntries(rows.map((row) => [row.key, row.value]));
  }
}

/** Deletions remembered for sync — see `Tombstone`. */
export class TombstoneRepository {
  private readonly db: FlightDataViewerDb;

  constructor(db: FlightDataViewerDb = defaultDb) {
    this.db = db;
  }

  async list(): Promise<Tombstone[]> {
    return this.db.tombstones.toArray();
  }

  async get(table: TombstoneTable, id: string): Promise<Tombstone | undefined> {
    return this.db.tombstones.get(tombstoneKey(table, id));
  }

  /** Keeps the later of the two deletion times when one is already recorded. */
  async record(table: TombstoneTable, id: string, deletedAt: string): Promise<void> {
    await this.db.transaction('rw', this.db.tombstones, async () => {
      await writeTombstone(this.db, table, id, deletedAt);
    });
  }

  async remove(table: TombstoneTable, id: string): Promise<void> {
    await this.db.tombstones.delete(tombstoneKey(table, id));
  }
}

/** Default instances bound to the singleton database. */
export const tracks = new TrackRepository();
export const trackFiles = new TrackFileRepository();
export const flights = new FlightRepository();
export const aircraft = new AircraftRepository();
export const settings = new SettingsRepository();
export const logbookEntries = new LogbookRepository();
export const logbookChecks = new LogbookCheckRepository();
export const tombstones = new TombstoneRepository();
